export const PUSH_DISMISS_STORAGE_KEY = 'hc:push-prompt:dismissed-at'
export const PUSH_SUBSCRIBE_FAILED_STORAGE_KEY = 'hc:push-prompt:subscribe-failed-at'

const SERVICE_WORKER_PATH = '/sw-push.js'
const SUBSCRIBE_ENDPOINT = '/api/push/subscribe'
const SUBSCRIBE_TIMEOUT_MS = 30_000

type DebugFn = (step: string, extra?: unknown) => void

let subscribeInFlight: Promise<PushSubscription> | null = null

export const isPushSupported = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const base64Standard = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64Standard)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

const subscriptionToJson = (
  subscription: PushSubscription,
): { endpoint: string; keys: { p256dh: string; auth: string } } | null => {
  const json = subscription.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return null
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  }
}

const sendSubscriptionToServer = async (subscription: PushSubscription): Promise<boolean> => {
  const body = subscriptionToJson(subscription)
  if (!body) return false

  const response = await fetch(SUBSCRIBE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })

  return response.ok
}

const subscribeOnce = async (
  registration: ServiceWorkerRegistration,
  publicKey: string,
  debug?: DebugFn,
): Promise<PushSubscription> => {
  const existing = await registration.pushManager.getSubscription()
  if (existing) return existing

  if (subscribeInFlight) return subscribeInFlight

  const applicationServerKey = urlBase64ToUint8Array(publicKey)
  if (typeof registration.pushManager.permissionState === 'function') {
    const pushPermissionState = await registration.pushManager.permissionState({
      userVisibleOnly: true,
      applicationServerKey,
    })
    debug?.('pushManager permissionState', pushPermissionState)
  }

  const subscribePromise = registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  })

  const timeoutPromise = new Promise<PushSubscription>((_, reject) => {
    window.setTimeout(
      () => reject(new Error(`pushManager.subscribe() timed out after ${SUBSCRIBE_TIMEOUT_MS}ms`)),
      SUBSCRIBE_TIMEOUT_MS,
    )
  })

  subscribeInFlight = Promise.race([subscribePromise, timeoutPromise]).finally(() => {
    subscribeInFlight = null
  })

  return subscribeInFlight
}

export async function syncPushSubscription({
  debug,
  publicKey,
}: {
  debug?: DebugFn
  publicKey: string
}): Promise<boolean> {
  debug?.('register service worker', SERVICE_WORKER_PATH)
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {
    scope: '/',
  })
  await navigator.serviceWorker.ready
  debug?.('service worker ready', registration.scope)

  debug?.('subscribe (or reuse) via subscribeOnce')
  const subscription = await subscribeOnce(registration, publicKey, debug)
  debug?.('subscription endpoint', subscription.endpoint)

  const ok = await sendSubscriptionToServer(subscription)
  debug?.('server sync ok?', ok)
  return ok
}
